import { IFieldMetadata } from "Model/Configuration/IFieldMetadata";
import { EntityType } from "Model/EntityType";
import { MissingFields } from "Model/MissingFields";
import { IItem } from "Model/Storage/IItem";
import { IProject } from "Model/Storage/IProject";
import { hasElements, hasNoElements, toSafeArray } from "./Array";
import { Configuration } from "./Configuration";
import { isCompany } from "./IsType";
import { getItemType, getName } from "./Item";
import { forEachRelevant } from "./Relevance";
import { isNullOrWhiteSpace, isString } from "./String";

export function getHasRoles(projectList: IProject | IProject[]): boolean {
  const safeList = toSafeArray(projectList);

  return safeList.every((project) => {
    if (project == null) {
      return false;
    }

    let hasRoles = true;
    forEachRelevant(project, (item) => {
      if (isCompany(item) && hasNoElements(item[`@targetRoles`])) {
        hasRoles = false;
      }

      return null;
    });

    return hasRoles;
  });
}

export function getMissingFieldList(projectList: IProject | IProject[], config: Configuration): MissingFields[] {
  const safeList = toSafeArray(projectList);

  let hasAllRequiredFieldsSet = true;
  const requiredList = safeList
    .map((project) => {
      if (project == null) {
        return null;
      }

      const requiredProjectFields: MissingFields = {
        name: getName(project),
        type: EntityType.Project,
        missingFields: [],
        children: []
      };

      forEachRelevant(project, (item) => {
        const type = getItemType(item);
        const requiredFields = config.getRequiredFields(type);
        const missingFields = getMissingFields(item, requiredFields);

        if (missingFields == null) {
          return null;
        }

        hasAllRequiredFieldsSet = false;

        if (type === EntityType.Project) {
          requiredProjectFields.missingFields = missingFields.missingFields;
          return null;
        }

        requiredProjectFields.children.push(missingFields);

        return null;
      });

      return requiredProjectFields;
    })
    .filter((f) => hasElements(f?.children) || hasElements(f?.missingFields));

  if (hasAllRequiredFieldsSet) {
    return [];
  }

  return requiredList;
}

export function getMissingFields(item: IItem, requiredFields: IFieldMetadata[]): MissingFields {
  if (hasNoElements(requiredFields)) {
    return null;
  }

  if (item == null) {
    return {
      name: getName(item),
      type: getItemType(item),
      missingFields: requiredFields.map((fieldMeta) => fieldMeta.fieldName)
    };
  }

  const missingFields = requiredFields
    .filter((fieldMeta) => {
      const value = item[fieldMeta.fieldName];

      if (Array.isArray(value)) {
        return hasNoElements(value);
      }

      if (isString(value)) {
        return isNullOrWhiteSpace(value);
      }

      return value == null;
    })
    .map((fieldMeta) => fieldMeta.fieldName);

  if (hasNoElements(missingFields)) {
    return null;
  }

  return {
    name: getName(item),
    type: getItemType(item),
    missingFields
  };
}

export function toIdList(projects: IProject[]) {
  if (hasNoElements(projects)) {
    return [];
  }

  return projects.map((p) => p[`@id`]);
}
